import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../config/supabase';
import { mockData } from '../data/mockData';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [statistics, setStatistics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch services
  const fetchServices = async () => {
    if (!isSupabaseConfigured) {
      setServices(mockData.services);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      setServices(data || []);
    } catch (error) {
      console.error('Error fetching services:', error);
      setError(error.message);
      // Fallback to mock data on error
      setServices(mockData.services);
    }
  };

  // Fetch testimonials
  const fetchTestimonials = async () => {
    if (!isSupabaseConfigured) {
      setTestimonials(mockData.testimonials);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('testimonials')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setTestimonials(data || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      setError(error.message);
      // Fallback to mock data on error
      setTestimonials(mockData.testimonials);
    }
  };

  // Fetch FAQs
  const fetchFaqs = async () => {
    if (!isSupabaseConfigured) {
      setFaqs(mockData.faqs);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('faqs')
        .select('*')
        .eq('is_active', true)
        .order('order_index', { ascending: true });
      
      if (error) throw error;
      setFaqs(data || []);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      setError(error.message);
      // Fallback to mock data on error
      setFaqs(mockData.faqs);
    }
  };

  // Fetch statistics
  const fetchStatistics = async () => {
    if (!isSupabaseConfigured) {
      setStatistics(mockData.statistics);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('statistics')
        .select('*')
        .eq('is_active', true)
        .order('order_index', { ascending: true });
      
      if (error) throw error;
      setStatistics(data || []);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      setError(error.message);
      // Fallback to mock data on error
      setStatistics(mockData.statistics);
    }
  };

  // Submit contact form
  const submitContact = async (contactData) => {
    if (!isSupabaseConfigured) {
      console.log('Contact form submission (mock):', contactData);
      return { success: true, data: { ...contactData, id: Date.now() } };
    }
    
    try {
      const { data, error } = await supabase
        .from('contacts')
        .insert([contactData])
        .select();
      
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error submitting contact:', error);
      return { success: false, error: error.message };
    }
  };

  // Subscribe to newsletter
  const subscribeNewsletter = async (email) => {
    if (!isSupabaseConfigured) {
      console.log('Newsletter subscription (mock):', email);
      return { success: true, data: { email, id: Date.now() } };
    }
    
    try {
      const { data, error } = await supabase
        .from('newsletter_subscriptions')
        .insert([{ email }])
        .select();
      
      if (error) {
        if (error.code === '23505') {
          return { success: false, error: 'Este email ya está suscrito' };
        }
        throw error;
      }
      return { success: true, data };
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      return { success: false, error: error.message };
    }
  };

  // Load all data on mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchServices(),
        fetchTestimonials(),
        fetchFaqs(),
        fetchStatistics()
      ]);
      setLoading(false);
    };

    loadData();
  }, []);

  const value = {
    services,
    testimonials,
    faqs,
    statistics,
    loading,
    error,
    submitContact,
    subscribeNewsletter,
    refreshData: {
      services: fetchServices,
      testimonials: fetchTestimonials,
      faqs: fetchFaqs,
      statistics: fetchStatistics
    }
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};